import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import UnitRoomsTab from "@/components/UnitRoomsTab";
import { UnitLinksPanel, unitTypeText } from "@/components/units/UnitLinks";
import { unitLabels } from "@/components/units/CommercialUnitFields";
import { UNIT_STATUS, TENANCY_STATUS, METER_TYPES, formatDate, formatEUR } from "@/lib/constants";
import { calibrationInfo } from "@/components/meters/MeterExtras";
import { UNIT_KINDS, FLOOR_CODES } from "@/lib/domivus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, ArrowRight, HouseLine, Calendar, Clock, ClockCounterClockwise,
  Buildings, Key, Gauge, FileText, ImageSquare, PencilSimple, FloppyDisk, X,
  UploadSimple, DownloadSimple, Trash, PaperPlaneTilt, Storefront,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";
import { NewListingDialog } from "@/components/marketing/ListingEditor";

const TABS = [
  { key: "overview",   label: "Übersicht",         icon: HouseLine },
  { key: "master",     label: "Stammdaten",        icon: Buildings },
  { key: "rooms",      label: "Räume",             icon: HouseLine },
  { key: "links",      label: "Verbundene Einheiten", icon: Buildings },
  { key: "tenancies",  label: "Mietverhältnisse",  icon: Calendar },
  { key: "keys",       label: "Schlüssel",         icon: Key },
  { key: "meters",     label: "Zähler",            icon: Gauge },
  { key: "documents",  label: "Dokumente",         icon: FileText },
  { key: "photos",     label: "Fotos",             icon: ImageSquare },
];

export default function UnitDetail() {
  const { propertyId, unitId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [tenancies, setTenancies] = useState([]);
  const [tab, setTab] = useState("overview");
  const [msgOpen, setMsgOpen] = useState(false);
  const [listingOpen, setListingOpen] = useState(false);
  const [mkMeta, setMkMeta] = useState(null);

  const load = async () => {
    const [{ data: p }, { data: t }] = await Promise.all([
      api.get(`/properties/${propertyId}`),
      api.get(`/tenancies`, { params: { unit_id: unitId, include_final_closed: true } }),
    ]);
    setProperty(p);
    setTenancies(t);
  };

  useEffect(() => { load().catch(() => { toast.error("Einheit oder Objekt nicht gefunden"); navigate("/objekte"); }); /* eslint-disable-next-line */ }, [propertyId, unitId]);

  const unit = useMemo(() => (property?.units || []).find((u) => u.id === unitId), [property, unitId]);

  if (!property) return <div className="text-sm text-slate-500">Lade Einheit…</div>;
  if (!unit) return <div className="text-sm text-rose-600">Einheit nicht gefunden.</div>;

  const status = UNIT_STATUS[unit.status] || { label: unit.status, className: "bg-slate-100" };
  // 2026-09 · Anzahl Eigentümer der Einheit
  const ownerCount =
    (unit.owner_contact_id ? 1 : 0) + ((unit.ownership_shares || []).length);
  const isWegLike = property.object_type === "weg" || property.object_type === "mixed" || property.object_type === "seg";

  return (
    <div className="space-y-4" data-testid="unit-detail">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <button
          type="button"
          onClick={() => { if (window.history.length > 1) navigate(-1); else navigate(`/objekte/${propertyId}`); }}
          className="hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="unit-back-btn"
        >
          <ArrowLeft size={14} /> Zurück
        </button>
        <span className="text-slate-300">·</span>
        <Link to="/objekte" className="hover:text-slate-900">Objekte</Link>
        <span className="text-slate-300">/</span>
        <Link to={`/objekte/${propertyId}`} className="hover:text-slate-900 truncate max-w-[280px]">{property.name}</Link>
        <span className="text-slate-300">/</span>
        <span className="text-slate-900 font-mono">{unit.unit_number}</span>
      </div>

      {/* Header + Tab-Leiste */}
      <div className="bg-white/90 backdrop-blur border border-stone-200 rounded-md p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold font-mono">
              Einheit · {unit.unit_number}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 mt-0.5 flex items-center gap-2">
              <HouseLine size={22} className="text-slate-500" />
              {unitTypeText(unit) !== "—" ? unitTypeText(unit) : "Einheit"} {unit.location ? `· ${unit.location}` : ""}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {property.address?.street} {property.address?.house_number}, {property.address?.zip} {property.address?.city}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setListingOpen(true); if (!mkMeta) api.get("/marketing/meta").then(({ data }) => setMkMeta(data)).catch(() => {}); }} data-testid="unit-create-listing-btn">
              <Storefront size={14} /> Inserat schalten
            </Button>
            <NewListingDialog open={listingOpen} onOpenChange={setListingOpen} meta={mkMeta} presetPropertyId={unit.property_id || property.id} presetUnit={unit}
              onCreated={(l) => navigate(`/vermarktung/inserat/${l.id}?tab=edit`)} />
            {isWegLike && ownerCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setMsgOpen(true)}
                data-testid="unit-write-to-owners-btn"
              >
                <PaperPlaneTilt size={14} /> An Eigentümer{ownerCount > 1 ? ` (${ownerCount})` : ""} schreiben
              </Button>
            )}
            <span className={`text-xs px-3 py-1 rounded-full ${status.className}`}>{status.label}</span>
          </div>
        </div>

        <nav className="mt-5 -mx-5 -mb-5 px-2 pt-2 border-t border-stone-200 bg-stone-50/70 rounded-b-md flex flex-wrap gap-1 overflow-x-auto" data-testid="unit-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-sm px-3 py-2 rounded-t-md flex items-center gap-2 transition-colors whitespace-nowrap ${
                  active
                    ? "bg-white text-slate-900 border-x border-t border-stone-200 -mb-px"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                }`}
                data-testid={`unit-tab-${t.key}`}
              >
                <Icon size={14} weight={active ? "fill" : "regular"} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {tab === "overview"  && <OverviewTab unit={unit} tenancies={tenancies} navigate={navigate} />}
      {tab === "master"    && <MasterTab unit={unit} onChange={load} />}
      {tab === "rooms"     && <UnitRoomsTab unitId={unit.id} />}
      {tab === "links"     && <div className="bg-white border border-stone-200 rounded-md p-4"><UnitLinksPanel unit={unit} /></div>}
      {tab === "tenancies" && <TenanciesTab tenancies={tenancies} navigate={navigate} unitId={unit.id} />}
      {tab === "keys"      && <KeysSection unit={unit} property={property} />}
      {tab === "meters"    && <MetersSection unit={unit} property={property} />}
      {tab === "documents" && <DocumentsSection unit={unit} category="Allgemein" />}
      {tab === "photos"    && <PhotosSection unit={unit} />}

      <MessagingDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        context={{ unit_id: unit.id, property_id: property.id }}
        defaultSubject={`Einheit ${unit.unit_number}`}
      />
    </div>
  );
}

function OverviewTab({ unit, tenancies, navigate }) {
  const today = new Date().toISOString().slice(0, 10);
  const active = tenancies.find((t) => t.status === "active" || t.status === "handover");
  // 2026-02 (feb) — Historie: alle zukünftigen und ehemaligen Mietverhältnisse
  // direkt in der Übersicht auflisten (nicht nur zählen).
  const futureList = tenancies.filter((t) => (t.status === "draft" || t.status === "reserved") && (t.contract_start || "9999") > today);
  const pastList = tenancies.filter((t) =>
    ["terminated", "ended", "archived"].includes(t.status) ||
    (t.contract_end && t.contract_end < today && !["active", "handover"].includes(t.status))
 ).sort((a, b) => (b.contract_end || b.contract_start || "").localeCompare(a.contract_end || a.contract_start || ""));

  return (
    <div className="space-y-4">
      <Card title="Schnellüberblick">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <Tile label={unitLabels(unit.unit_type, unit.unit_kind).area} value={(unit.living_area || unit.usable_area) ? `${unit.living_area || unit.usable_area} m²` : "—"} />
          <Tile label={unitLabels(unit.unit_type, unit.unit_kind).rooms} value={unit.rooms ?? "—"} />
          <Tile label="Schlafz." value={unit.bedrooms ?? "—"} />
          <Tile label="Bäder" value={unit.bathrooms ?? "—"} />
          <Tile label="Geschoss" value={FLOOR_CODES[unit.floor_code] || unit.floor_code || "—"} />
          <Tile label="MEA" value={unit.mea_share ? `${unit.mea_share}/1000` : "—"} />
        </div>
      </Card>
      <Card title="Aktuelles Mietverhältnis">
        {active ? (
          <div
            className="flex items-center justify-between cursor-pointer hover:bg-stone-50 -mx-2 -my-1 px-2 py-1 rounded-sm"
            onClick={() => navigate?.(`/mietverhaeltnisse/${active.id}`)}
            data-testid={`overview-active-tenancy-${active.id}`}
          >
            <div>
              <div className="text-xs text-slate-500 font-mono">{active.contract_number}</div>
              <div className="text-sm font-medium text-slate-900 mt-0.5">
                {(active.tenant_names || []).join(", ") || "—"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {active.contract_start ? `Beginn: ${formatDate(active.contract_start)}` : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm tabular-nums font-medium text-slate-900">{formatEUR(active.rent_cold)}</div>
              <div className="text-[11px] text-slate-500">Kaltmiete</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500">Aktuell vermietet: nein</div>
        )}
      </Card>

      {/* 2026-02 (feb) — Zukünftige Mietverhältnisse direkt anzeigen */}
      {futureList.length > 0 && (
        <TenancyGroup title="Zukünftige Mietverhältnisse" icon={Calendar}
          empty="" items={futureList}
          onOpen={(t) => navigate?.(`/mietverhaeltnisse/${t.id}`)} />
      )}

      {/* 2026-02 (feb) — Ehemalige Mietverhältnisse direkt anzeigen */}
      {pastList.length > 0 && (
        <TenancyGroup title="Ehemalige Mietverhältnisse" icon={ClockCounterClockwise}
          empty="" items={pastList}
          onOpen={(t) => navigate?.(`/mietverhaeltnisse/${t.id}`)} />
      )}
    </div>
  );
}

// ============================================================
// Stammdaten Tab — editierbar (PATCH /units/{id})
// ============================================================
function MasterTab({ unit, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => masterFormFrom(unit));
  useEffect(() => { setForm(masterFormFrom(unit)); }, [unit]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const str = (v) => (v === "" ? null : v);

  const save = async () => {
    try {
      const payload = {
        unit_kind: Number(form.unit_kind),
        floor_code: form.floor_code,
        position: Number(form.position) || 1,
        location: str(form.location),
        living_area: num(form.living_area),
        usable_area: num(form.usable_area),
        rooms: num(form.rooms),
        bedrooms: num(form.bedrooms),
        bathrooms: num(form.bathrooms),
        has_separate_wc: form.has_separate_wc,
        has_kitchen: form.has_kitchen,
        has_balcony: form.has_balcony,
        balcony_area: num(form.balcony_area),
        has_terrace: form.has_terrace,
        has_garden: form.has_garden,
        has_basement_room: form.has_basement_room,
        has_attic_room: form.has_attic_room,
        parking_spots: num(form.parking_spots),
        mea_share: num(form.mea_share),
        status: form.status,
        default_rent_cold: num(form.default_rent_cold),
        default_utilities_advance: num(form.default_utilities_advance),
        default_heating_advance: num(form.default_heating_advance),
        notes: str(form.notes),
        property_id: unit.property_id,
        unit_type: unit.unit_type || "apartment",
      };
      await api.patch(`/properties/units/${unit.id}`, payload);
      toast.success("Einheit gespeichert");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  if (!editing) {
    return (
      <Card
        title="Stammdaten der Einheit"
        action={
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(true)} data-testid="edit-unit-master-btn">
            <PencilSimple size={14} /> Bearbeiten
          </Button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <Tile label="Typ" value={UNIT_KINDS[unit.unit_kind] || "—"} />
          <Tile label="Status" value={(UNIT_STATUS[unit.status] || {}).label || unit.status} />
          <Tile label="Wohnfl." value={unit.living_area ? `${unit.living_area} m²` : "—"} />
          <Tile label="Nutzfl." value={unit.usable_area ? `${unit.usable_area} m²` : "—"} />
          <Tile label="Zimmer" value={unit.rooms ?? "—"} />
          <Tile label="Schlafz." value={unit.bedrooms ?? "—"} />
          <Tile label="Bäder" value={unit.bathrooms ?? "—"} />
          <Tile label="Geschoss" value={FLOOR_CODES[unit.floor_code] || "—"} />
          <Tile label="MEA" value={unit.mea_share ? `${unit.mea_share}/1000` : "—"} />
          <Tile label="Balkon" value={unit.has_balcony ? (unit.balcony_area ? `${unit.balcony_area} m²` : "Ja") : "—"} />
          <Tile label="Terrasse" value={unit.has_terrace ? "Ja" : "—"} />
          <Tile label="Garten" value={unit.has_garden ? "Ja" : "—"} />
          <Tile label="EBK" value={unit.has_kitchen ? "Ja" : "—"} />
          <Tile label="Kellerraum" value={unit.has_basement_room ? "Ja" : "—"} />
          <Tile label="Stellplätze" value={unit.parking_spots ?? "—"} />
          <Tile label="Richtmiete" value={unit.default_rent_cold ? formatEUR(unit.default_rent_cold) : "—"} />
        </div>
        {unit.notes && (
          <div className="mt-4 p-3 bg-stone-50 border border-stone-100 rounded-sm text-sm text-slate-700 whitespace-pre-wrap">{unit.notes}</div>
        )}
      </Card>
    );
  }

  return (
    <Card
      title="Stammdaten bearbeiten"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => { setEditing(false); setForm(masterFormFrom(unit)); }}><X size={14} /> Abbrechen</Button>
          <Button size="sm" className="gap-1.5 h-8 bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-unit-master-btn"><FloppyDisk size={14} /> Speichern</Button>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <FF label="Einheitstyp">
          <Select value={String(form.unit_kind)} onValueChange={(v) => set("unit_kind", Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(UNIT_KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </FF>
        <FF label="Status">
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(UNIT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </FF>
        <FF label="Geschoss">
          <Select value={form.floor_code} onValueChange={(v) => set("floor_code", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FLOOR_CODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </FF>
      </div>
      <FF label="Lage / Bezeichnung" className="mt-3"><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></FF>
      <div className="grid grid-cols-4 gap-3 mt-3">
        <FF label="Wohnfl. (m²)"><Input type="number" step="0.1" value={form.living_area} onChange={(e) => set("living_area", e.target.value)} /></FF>
        <FF label="Nutzfl. (m²)"><Input type="number" step="0.1" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} /></FF>
        <FF label="Zimmer"><Input type="number" step="0.5" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} /></FF>
        <FF label="Schlafz."><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></FF>
        <FF label="Bäder"><Input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} /></FF>
        <FF label="Balkon (m²)"><Input type="number" step="0.1" value={form.balcony_area} onChange={(e) => set("balcony_area", e.target.value)} /></FF>
        <FF label="Stellplätze"><Input type="number" value={form.parking_spots} onChange={(e) => set("parking_spots", e.target.value)} /></FF>
        <FF label="MEA (1000-stel)"><Input type="number" value={form.mea_share} onChange={(e) => set("mea_share", e.target.value)} /></FF>
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <Toggle label="Sep. WC" v={form.has_separate_wc} on={(v) => set("has_separate_wc", v)} />
        <Toggle label="EBK" v={form.has_kitchen} on={(v) => set("has_kitchen", v)} />
        <Toggle label="Balkon" v={form.has_balcony} on={(v) => set("has_balcony", v)} />
        <Toggle label="Terrasse" v={form.has_terrace} on={(v) => set("has_terrace", v)} />
        <Toggle label="Garten" v={form.has_garden} on={(v) => set("has_garden", v)} />
        <Toggle label="Kellerraum" v={form.has_basement_room} on={(v) => set("has_basement_room", v)} />
        <Toggle label="Speicher" v={form.has_attic_room} on={(v) => set("has_attic_room", v)} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <FF label="Kaltmiete € (Richtwert)"><Input type="number" step="0.01" value={form.default_rent_cold} onChange={(e) => set("default_rent_cold", e.target.value)} /></FF>
        <FF label="NK-VZ € (Richtwert)"><Input type="number" step="0.01" value={form.default_utilities_advance} onChange={(e) => set("default_utilities_advance", e.target.value)} /></FF>
        <FF label="HK-VZ € (Richtwert)"><Input type="number" step="0.01" value={form.default_heating_advance} onChange={(e) => set("default_heating_advance", e.target.value)} /></FF>
      </div>
      <FF label="Notizen" className="mt-3"><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FF>
    </Card>
  );
}

function masterFormFrom(u) {
  return {
    unit_kind: u.unit_kind || 1,
    floor_code: u.floor_code || "00",
    position: u.position || 1,
    location: u.location || "",
    living_area: u.living_area ?? "",
    usable_area: u.usable_area ?? "",
    rooms: u.rooms ?? "",
    bedrooms: u.bedrooms ?? "",
    bathrooms: u.bathrooms ?? "",
    has_separate_wc: !!u.has_separate_wc,
    has_kitchen: !!u.has_kitchen,
    has_balcony: !!u.has_balcony,
    balcony_area: u.balcony_area ?? "",
    has_terrace: !!u.has_terrace,
    has_garden: !!u.has_garden,
    has_basement_room: !!u.has_basement_room,
    has_attic_room: !!u.has_attic_room,
    parking_spots: u.parking_spots ?? "",
    mea_share: u.mea_share ?? "",
    status: u.status || "vacant",
    default_rent_cold: u.default_rent_cold ?? "",
    default_utilities_advance: u.default_utilities_advance ?? "",
    default_heating_advance: u.default_heating_advance ?? "",
    notes: u.notes || "",
  };
}

// ============================================================
// Mietverhältnisse Tab
// ============================================================
function TenanciesTab({ tenancies, navigate, unitId }) {
  const today = new Date().toISOString().slice(0, 10);
  const active = tenancies.filter((t) => t.status === "active" || t.status === "handover");
  const future = tenancies.filter((t) => (t.status === "draft" || t.status === "reserved") && (t.contract_start || "9999") > today);
  const past = tenancies.filter((t) =>
    ["terminated", "ended", "archived"].includes(t.status) ||
    (t.contract_end && t.contract_end < today && !["active", "handover"].includes(t.status))
 ).sort((a, b) => (b.contract_end || b.contract_start || "").localeCompare(a.contract_end || a.contract_start || ""));
  const others = tenancies.filter((t) => !active.includes(t) && !future.includes(t) && !past.includes(t));

  return (
    <div className="space-y-4">
      <TenancyGroup title="Aktuelles Mietverhältnis" icon={Clock} emphasis
        empty="Aktuell ist kein Mietverhältnis aktiv."
        items={active} onOpen={(t) => navigate(`/mietverhaeltnisse/${t.id}`)} />
      <TenancyGroup title="Zukünftige Mietverhältnisse" icon={Calendar}
        empty="Keine zukünftigen Mietverhältnisse geplant."
        items={future} onOpen={(t) => navigate(`/mietverhaeltnisse/${t.id}`)} />
      <TenancyGroup title="Ehemalige Mietverhältnisse" icon={ClockCounterClockwise}
        empty="Noch keine vergangenen Mietverhältnisse."
        items={past} onOpen={(t) => navigate(`/mietverhaeltnisse/${t.id}`)} />
      {others.length > 0 && (
        <TenancyGroup title="Sonstige Mietverhältnisse" icon={ClockCounterClockwise}
          empty="" items={others} onOpen={(t) => navigate(`/mietverhaeltnisse/${t.id}`)} />
      )}
    </div>
  );
}

function KeysSection({ unit, property }) {
  const keys = (property.keys || []).filter((k) => k.unit_id === unit.id);
  return (
    <Card
      title={`Schlüssel der Einheit (${keys.length})`}
      action={
        <Link to={`/objekte/${property.id}?tab=keys`} className="text-xs text-slate-600 hover:text-slate-900">
          Im Objekt verwalten →
        </Link>
      }
    >
      {keys.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">
          Dieser Einheit sind keine Schlüssel zugeordnet.<br />
          <span className="text-xs">Schlüssel werden im Objekt unter „Schlüssel" gepflegt.</span>
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-md overflow-hidden">
          {keys.map((k) => (
            <li key={k.id} className="px-3 py-2 flex items-center gap-3 text-sm hover:bg-stone-50" data-testid={`unit-key-${k.id}`}>
              <Key size={14} className="text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 truncate">
                  <span className="font-medium">{k.label}</span>
                  {k.key_number ? <span className="text-slate-500 font-mono ml-2">#{k.key_number}</span> : null}
                </div>
                <div className="text-[11px] text-slate-500">
                  Anzahl: {k.count} {k.holder_type ? `· ${k.holder_type}` : ""} {k.given_at ? `· übergeben ${formatDate(k.given_at)}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MetersSection({ unit, property }) {
  const meters = (property.meters || []).filter((m) => m.unit_id === unit.id || (m.unit_ids || []).includes(unit.id));
  return (
    <Card
      title={`Zähler der Einheit (${meters.length})`}
      action={
        <Link to={`/objekte/${property.id}?tab=meters`} className="text-xs text-slate-600 hover:text-slate-900">
          Im Objekt verwalten →
        </Link>
      }
    >
      {meters.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">
          Dieser Einheit sind keine Zähler zugeordnet.<br />
          <span className="text-xs">Zähler werden im Objekt unter „Zähler & Werte" gepflegt.</span>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-700 text-left">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Nr.</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Art</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Einheit</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Letzte Ablesung</th>
            </tr>
          </thead>
          <tbody>
            {meters.map((m) => {
              const last = (m.readings || []).slice().sort((a, b) => (a.reading_date < b.reading_date ? 1 : -1))[0];
              return (
                <tr key={m.id} className={`border-t border-stone-100 ${m.active === false ? "opacity-60" : ""}`} data-testid={`unit-meter-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono">{m.meter_number}
                    {m.active === false && <div className="font-sans text-[10px] text-slate-500">ausgebaut {m.removed_at ? formatDate(m.removed_at) : ""}</div>}
                    {m.planned_change && <div className="font-sans text-[10px] text-sky-700">Wechsel geplant {formatDate(m.planned_change.change_date)}</div>}
                    {(() => { const c = calibrationInfo(m); return c ? <div className={`font-sans text-[10px] ${c.expired ? "text-rose-700 font-semibold" : "text-slate-500"}`}>Eichung {c.label}{c.until ? ` · bis Ende ${c.until}` : ""}</div> : null; })()}
                  </td>
                  <td className="px-3 py-2">{METER_TYPES[m.meter_type] || "—"}</td>
                  <td className="px-3 py-2">{m.measurement_unit || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {last ? `${formatDate(last.reading_date)} · ${last.value} ${m.measurement_unit || ""}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function DocumentsSection({ unit, category }) {
  return (
    <Card title="Dokumente der Einheit">
      <DocumentsList unitId={unit.id} propertyId={unit.property_id} category={category} accept="*" />
    </Card>
  );
}

function PhotosSection({ unit }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Innenansicht");
  const categories = ["Innenansicht", "Außenansicht", "Bad", "Küche", "Schlafzimmer", "Wohnzimmer", "Schaden/Mangel", "Übergabe", "Sonstige"];

  const load = async () => {
    const { data } = await api.get("/documents", { params: { property_id: unit.property_id, category: "Foto" } });
    setPhotos(data.filter((d) => d.linked_unit_id === unit.id));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unit.id]);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    fd.append("category", "Foto");
    fd.append("doc_type", "photo");
    fd.append("notes", category);
    fd.append("linked_property_id", unit.property_id);
    fd.append("linked_unit_id", unit.id);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto hochgeladen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    if (!confirm("Foto wirklich löschen?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const photoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download`;
  const grouped = categories.map((cat) => ({ cat, items: photos.filter((p) => p.notes === cat) })).filter((g) => g.items.length > 0);
  const ungrouped = photos.filter((p) => !categories.includes(p.notes));

  return (
    <Card
      title={`Fotos der Einheit (${photos.length})`}
      action={
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-44" data-testid="photo-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md cursor-pointer" data-testid="photo-upload-btn">
            <UploadSimple size={14} />
            {uploading ? "Lade hoch…" : "Foto hochladen"}
            <input type="file" accept="image/*" className="hidden" disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
          </label>
        </div>
      }
    >
      {photos.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8">
          Noch keine Fotos vorhanden.<br />
          <span className="text-xs">Beispiel-Kategorien: Innenansicht, Bad, Küche, Schaden/Mangel, Übergabe …</span>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ cat, items }) => (
            <div key={cat}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{cat} ({items.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {items.map((p) => <PhotoCard key={p.id} photo={p} url={photoUrl(p.id)} onDelete={removePhoto} />)}
              </div>
            </div>
          ))}
          {ungrouped.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Ohne Kategorie</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {ungrouped.map((p) => <PhotoCard key={p.id} photo={p} url={photoUrl(p.id)} onDelete={removePhoto} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function PhotoCard({ photo, url, onDelete }) {
  return (
    <div className="group relative bg-stone-50 border border-stone-200 rounded-md overflow-hidden aspect-square" data-testid={`photo-${photo.id}`}>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img src={url} alt={photo.title} className="w-full h-full object-cover" loading="lazy" />
      </a>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-[10px] text-white truncate">{photo.title}</div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(photo.id)}
        className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white rounded text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash size={12} />
      </button>
    </div>
  );
}

function DocumentsList({ unitId, propertyId, category }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/documents", { params: { property_id: propertyId } });
    setDocs(data.filter((d) => d.linked_unit_id === unitId && d.category !== "Foto"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId, propertyId]);

  const handleUpload = async (file, cat) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    fd.append("category", cat || category);
    fd.append("linked_property_id", propertyId);
    fd.append("linked_unit_id", unitId);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hochgeladen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Dokument wirklich löschen?")) return;
    await api.delete(`/documents/${id}`);
    await load();
  };

  const url = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download`;

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md cursor-pointer" data-testid="unit-doc-upload-btn">
          <UploadSimple size={14} />
          {uploading ? "Lade hoch…" : "Hochladen"}
          <input type="file" className="hidden" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
        </label>
      </div>
      {docs.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6">Noch keine Dokumente vorhanden.</div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-md overflow-hidden">
          {docs.map((d) => (
            <li key={d.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-stone-50">
              <FileText size={14} className="text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-slate-900">{d.title}</div>
                <div className="text-[11px] text-slate-500">
                  {d.category} · {formatDate(d.created_at)}
                </div>
              </div>
              <a href={url(d.id)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-stone-100" title="Herunterladen"><DownloadSimple size={14} /></a>
              <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => handleDelete(d.id)} title="Löschen"><Trash size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// Small UI primitives
// ============================================================
function Card({ title, action, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-md shadow-sm">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between bg-stone-50/40 rounded-t-md">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-stone-50 border border-stone-100 rounded-sm px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-800 truncate" title={String(value)}>{value}</div>
    </div>
  );
}

function FF({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, v, on }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
      <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!v} onChange={(e) => on(e.target.checked)} />
      <span className="text-slate-700">{label}</span>
    </label>
  );
}

const duration = (a, b) => {
  const m = (new Date(b).getFullYear() - new Date(a).getFullYear()) * 12 + new Date(b).getMonth() - new Date(a).getMonth() + 1;
  return m >= 12 ? `${Math.floor(m / 12)} J. ${m % 12 ? `${m % 12} Mon.` : ""}`.trim() : `${Math.max(m, 0)} Mon.`;
};

function TenancyGroup({ title, icon: Icon, empty, items, emphasis, onOpen }) {
  return (
    <div className={`bg-white border ${emphasis ? "border-slate-300 shadow-md" : "border-stone-200 shadow-sm"} rounded-md`}>
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between bg-stone-50/40 rounded-t-md">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Icon size={16} weight="regular" className="text-slate-500" /> {title}
        </h2>
        <span className="text-xs text-slate-500">{items.length} {items.length === 1 ? "Eintrag" : "Einträge"}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">{empty}</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {items.map((t) => {
            const status = TENANCY_STATUS[t.status] || { label: t.status, className: "bg-slate-100" };
            const balance = t.balance ?? 0;
            return (
              <li key={t.id}
                className="px-5 py-3 hover:bg-stone-50 cursor-pointer flex items-center justify-between gap-4"
                onClick={() => onOpen(t)}
                data-testid={`tenancy-link-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500">{t.contract_number || "—"}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${status.className}`}>{status.label}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-900 mt-0.5 truncate">
                    {(t.tenant_names || []).join(", ") || <span className="text-slate-400">Keine Mieter zugeordnet</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t.contract_start ? `Beginn: ${formatDate(t.contract_start)}` : "—"}
                    {t.contract_end ? ` · Ende: ${formatDate(t.contract_end)}` : ""}
                    {t.contract_start && t.contract_end ? ` · Dauer: ${duration(t.contract_start, t.contract_end)}` : ""}
                    {t.final_closed ? " · abgeschlossen" : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm tabular-nums font-medium text-slate-900">{formatEUR(t.rent_cold)}</div>
                  <div className={`text-xs tabular-nums ${balance < 0 ? "text-rose-600" : balance > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                    Saldo: {formatEUR(balance)}
                  </div>
                </div>
                <ArrowRight size={14} className="text-slate-400 shrink-0" />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
